import { useRef, useEffect, useState } from 'react';
import { saveDocumentFormData } from '../services/auditFindingsService';

/**
 * Custom hook for auto-saving form data
 * Implements debounce to avoid excessive API calls
 *
 * @param {number} projectId - The project ID
 * @param {string} documentCode - The document code (e.g., 'FAPP-06')
 * @param {string} category - The document category
 * @param {object} formData - The form data to save
 * @param {number} debounceDelay - Delay in ms before saving (default 2000)
 * @param {boolean} enabled - Whether auto-save is enabled (default true)
 * @param {string} scopeKey - Optional scope key for multi-instance forms
 * @returns {object} - { isSaving, error, lastSavedAt, manualSave }
 */
export const useAutoSaveForm = (
  projectId,
  documentCode,
  category,
  formData,
  debounceDelay = 2000,
  enabled = true,
  scopeKey = ''
) => {
  const debounceTimer = useRef(null);
  const latestPayloadRef = useRef('');
  const latestMetaRef = useRef({ projectId, documentCode, category, scopeKey });
  const lastSavedDataRef = useRef('');
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    latestMetaRef.current = { projectId, documentCode, category, scopeKey };
  }, [projectId, documentCode, category, scopeKey]);

  useEffect(() => {
    latestPayloadRef.current = formData ? JSON.stringify(formData) : '';
  }, [formData]);

  const performSaveInternal = async () => {
    const meta = latestMetaRef.current;
    const currentStr = latestPayloadRef.current;

    if (!meta.projectId || !meta.documentCode || !currentStr) {
      return;
    }

    if (currentStr === lastSavedDataRef.current) {
      setStatus('saved');
      return;
    }

    try {
      setSaving(true);
      setStatus('saving');
      setError(null);

      const result = await saveDocumentFormData(meta.projectId, {
        documentCode: meta.documentCode,
        category: meta.category,
        formDataJson: currentStr,
        scopeKey: meta.scopeKey,
      });

      if (result?.success === false) {
        throw new Error(result.message || 'Auto-save failed');
      }

      lastSavedDataRef.current = currentStr;
      setLastSavedAt(new Date());
      setStatus('saved');
    } catch (err) {
      console.error('Auto-save error:', err);
      setError(err?.response?.data?.message || err.message || 'Auto-save failed');
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!enabled || !projectId || !documentCode || !latestPayloadRef.current) {
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      performSaveInternal();
    }, debounceDelay);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [formData, debounceDelay, enabled, projectId, documentCode, category, scopeKey]);

  return {
    isSaving,
    status,
    error,
    lastSavedAt,
    manualSave: performSaveInternal,
  };
};

export default useAutoSaveForm;
